import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { toArray } from 'redux/utils'
import { TAnalysisResponse } from 'redux/api/Analyses'
import { TAnalysisPropertyDto } from '@homepal/api-contract'
import { getParsedDate } from 'utils/dateParser'
import { EAnalysisStatus } from 'types/Enums'
import type { Lang } from 'language/translator'

export type TAnalysisSelectorResponse = TAnalysisResponse & {
  is_handled: boolean

  /** The stored title, without the date `title` gets. */
  name: string
  potential: number | null
  qualified_properties: TAnalysisPropertyDto[]
}

/**
 * The sum of `noi_change` (SEK) over the analysis' selected properties.
 * Null when no selected property carries a value.
 */
const getPotential = (analysis: TAnalysisResponse): number | null => {
  const values = analysis.properties
    .filter((property) => property.selected && property.noi_change !== null)
    .map((property) => property.noi_change as number)

  if (values.length === 0) {
    return null
  }

  return values.reduce((sum, value) => sum + value, 0)
}

/** An analysis as shown to users: "Vakanta lokaler | 1 okt". */
export const getAnalysisTitle = (
  analysis: TAnalysisResponse,
  lang: Lang
): string => `${analysis.title} – ${getParsedDate(analysis.created_at, lang)}`

/** A stored row plus the values derived from the row alone. */
const enrichAnalysis = (
  row: TAnalysisResponse,
  lang: Lang
): TAnalysisSelectorResponse => ({
  ...row,
  is_handled: row.status !== EAnalysisStatus.UNHANDLED,
  name: row.title,
  potential: getPotential(row),
  qualified_properties: row.properties.filter(
    (property) => property.classification !== 'not_qualified'
  ),
  title: getAnalysisTitle(row, lang)
})

/** Every stored analysis, newest first. */
export const selectAnalyses = createSelector(
  [
    (state: ApplicationState) => state.AnalysisStore.analyses,
    (_: ApplicationState, lang: Lang) => lang
  ],
  (analyses, lang): TAnalysisSelectorResponse[] =>
    toArray<TAnalysisResponse>(analyses)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .map((row) => enrichAnalysis(row, lang))
)

export const selectAnalysis = createSelector(
  [
    (state: ApplicationState) => state.AnalysisStore.analyses,
    (_: ApplicationState, id: string | null) => id,
    (_: ApplicationState, _id: string | null, lang: Lang) => lang
  ],
  (analyses, id, lang): TAnalysisSelectorResponse | null => {
    const row = id ? analyses[id] : undefined

    return row ? enrichAnalysis(row, lang) : null
  }
)

export const selectAnalysesFetchStatus = (state: ApplicationState) =>
  state.AnalysisStore.fetchStatus
